// src/components/TrackRecords/TrackRecords.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ViewTrackRecords = () => {
  // State for query parameters
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 50,
    order: "RT-desc",
    startTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // Default: 7 days ago
    endTime: new Date(),
    assetTypeId: '',
    guid: '',
    type: 1,
    dateType: 0,
    language: 0
  });

  // State for results
  const [trackRecords, setTrackRecords] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState('');

  // Format date for API
  const formatDateTime = (date) => {
    return new Date(date).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
  };

  // Parse and enhance track data
  const parseTrackRecord = (record) => ({
    longitude: record.Lon,
    latitude: record.Lat,
    speed: record.Speed,
    direction: record.Dir,
    mileage: record.Mil,
    locationType: ['unlocated', 'gnss', 'cell_track', 'cell_code', 'mobile'][record.LType] || 'unknown',
    cellInfo: {
      mcc: record.MCC,
      mnc: record.MNC,
      lac: record.LAC,
      cid: record.CID
    },
    gpsTime: record.GT,
    receiveTime: record.RT,
    battery: record.Bat,
    status: {
      lockRope: record.LS
    },
    temperature: {
      temp1: record.Temp1 === -1000 ? null : record.Temp1,
      temp2: record.Temp2 === -1000 ? null : record.Temp2
    },
    humidity: {
      hum1: record.Hum1,
      hum2: record.Hum2
    }
  });

  // Fetch track records
  const fetchTrackRecords = async () => {
    const newRequestId = uuidv4();
    setRequestId(newRequestId);
    setLoading(true);
    setError(null);
    setTrackRecords([]);

    try {
      // Validate required parameters
      if (!queryParams.guid || !queryParams.startTime || !queryParams.endTime || !queryParams.assetTypeId) {
        throw {
          error: 'Missing required parameters',
          required: ['guid', 'startTime', 'endTime', 'assetTypeId']
        };
      }

      const token = process.env.TOKEN_ID || "3585535a-137c-499e-82ad-af9feff7d8e7";
      const payload = {
        FAction: "QueryLBSTrackListByFGUID",
        FTokenID: token,
        FGUID: queryParams.guid,
        FType: parseInt(queryParams.type),
        FAssetTypeID: parseInt(queryParams.assetTypeId),
        FStartTime: formatDateTime(queryParams.startTime),
        FEndTime: formatDateTime(queryParams.endTime),
        FLanguage: parseInt(queryParams.language),
        FDateType: parseInt(queryParams.dateType)
      };

      // Try both endpoints with failover
      const endpoints = [
        'https://icloud.assetscontrols.com:3443/OpenApi/LBS',
        'http://icloud.assetscontrols.com:8092/OpenApi/LBS'
      ];

      let response;
      let lastError;

      for (const endpoint of endpoints) {
        try {
          response = await axios.post(endpoint, payload, {
            headers: {
              'Content-Type': 'application/json',
              'X-Request-ID': newRequestId
            },
            timeout: 10000
          });
          break; // Success, exit loop
        } catch (error) {
          lastError = error;
          continue; // Try next endpoint
        }
      }

      if (!response) {
        throw lastError || new Error('All API endpoints failed');
      }

      // Handle API errors
      if (response.data?.Result !== 200) {
        const errorMap = {
          102: { status: 400, message: 'Invalid parameters' },
          104: { status: 401, message: 'Token error or expiration' },
          105: { status: 400, message: 'Request error' }
        };

        const errorInfo = errorMap[response.data?.Result] || { 
          status: 500, 
          message: response.data?.Message || 'Unknown API error' 
        };

        throw {
          error: errorInfo.message,
          apiResponse: response.data
        };
      }

      // Process track records
      let tracks = response.data.FObject || [];

      // Apply sorting
      const [sortField, sortDirection] = queryParams.order.split('-');
      tracks.sort((a, b) => {
        const valA = a[sortField] || a[`F${sortField}`];
        const valB = b[sortField] || b[`F${sortField}`];
        return sortDirection === 'asc' 
          ? valA > valB ? 1 : -1 
          : valA < valB ? 1 : -1;
      });

      // Pagination
      const pageNumber = parseInt(queryParams.page);
      const pageLimit = parseInt(queryParams.limit);
      const total = tracks.length;
      const pageCount = Math.ceil(total / pageLimit);
      const offset = pageNumber > 1 ? (pageNumber - 1) * pageLimit : 0;

      const paginatedTracks = tracks.slice(offset, offset + pageLimit).map(parseTrackRecord);

      setTrackRecords(paginatedTracks);
      setPagination({
        total,
        total_docs: paginatedTracks.length,
        pages: pageCount,
        hasNextPage: pageCount > pageNumber,
        hasPrevPage: pageNumber > 1,
        currentPage: pageNumber
      });

    } catch (err) {
      console.error('Track records error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle parameter changes
  const handleParamChange = (e) => {
    const { name, value } = e.target;
    setQueryParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date changes
  const handleDateChange = (date, field) => {
    setQueryParams(prev => ({
      ...prev,
      [field]: date
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setQueryParams(prev => ({
      ...prev,
      page
    }));
  };

  // Submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchTrackRecords();
  };

  // Load data when page changes
  useEffect(() => {
    if (queryParams.page > 1) {
      fetchTrackRecords();
    }
  }, [queryParams.page]);

  return (
    <div className="track-records-container p-4 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Track Records</h2>
      
      {/* Query Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device GUID</label>
            <input
              type="text"
              name="guid"
              value={queryParams.guid}
              onChange={handleParamChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type ID</label>
            <input
              type="number"
              name="assetTypeId"
              value={queryParams.assetTypeId}
              onChange={handleParamChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              name="type"
              value={queryParams.type}
              onChange={handleParamChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="1">Type 1</option>
              <option value="2">Type 2</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
            <DatePicker
              selected={queryParams.startTime}
              onChange={(date) => handleDateChange(date, 'startTime')}
              showTimeSelect
              dateFormat="Pp"
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
            <DatePicker
              selected={queryParams.endTime}
              onChange={(date) => handleDateChange(date, 'endTime')}
              showTimeSelect
              dateFormat="Pp"
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
            <select
              name="order"
              value={queryParams.order}
              onChange={handleParamChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="RT-desc">Receive Time (Newest First)</option>
              <option value="RT-asc">Receive Time (Oldest First)</option>
              <option value="GT-desc">GPS Time (Newest First)</option>
              <option value="GT-asc">GPS Time (Oldest First)</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Items per page:</label>
            <select
              name="limit"
              value={queryParams.limit}
              onChange={handleParamChange}
              className="p-2 border border-gray-300 rounded"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            {loading ? 'Loading...' : 'Search'}
          </button>
        </div>
      </form>

      {/* Loading State */}
      {loading && (
        <div className="loading-indicator p-4 bg-blue-50 rounded mb-4">
          <p>Loading track records...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-message p-4 bg-red-50 border-l-4 border-red-500 rounded mb-4">
          <h3 className="font-bold text-red-700">
            {error.error || 'Error fetching track records'}
          </h3>
          
          {error.required && (
            <div className="mt-2">
              <p className="text-sm">Required parameters:</p>
              <ul className="list-disc list-inside ml-4">
                {error.required.map(param => (
                  <li key={param}>{param}</li>
                ))}
              </ul>
            </div>
          )}
          
          {error.apiResponse && (
            <div className="mt-3 p-2 bg-gray-100 rounded text-xs">
              <pre>{JSON.stringify(error.apiResponse, null, 2)}</pre>
            </div>
          )}
          
          <div className="mt-3 text-xs text-gray-500">
            Request ID: {requestId}
          </div>
        </div>
      )}

      {/* Results */}
      {trackRecords.length > 0 && (
        <div className="results-container">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {/* Pagination Header */}
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <div>
                Showing {trackRecords.length} of {pagination.total} records
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1">
                  Page {pagination.currentPage} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
            
            {/* Track Records Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Speed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mileage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Battery
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trackRecords.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(record.receiveTime).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          GPS: {new Date(record.gpsTime).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {record.latitude}, {record.longitude}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.locationType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {record.speed} km/h
                        </div>
                        <div className="text-xs text-gray-500">
                          Dir: {record.direction}°
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.mileage} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.battery}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          Lock: {record.status.lockRope ? 'Yes' : 'No'}
                        </div>
                        {record.temperature.temp1 !== null && (
                          <div className="text-xs text-gray-500">
                            Temp: {record.temperature.temp1}°C
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewTrackRecords;